import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { menuItems } from '@/core/constants/menu-items.constant'
import { useAuthStore } from '@/modules/auth/stores/auth.store'

export function useMenu() {
  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()
  const activeModule = ref(null)
  const expandedItems = ref(new Set())

  const isRouteActive = (routePath) => {
    return route.path === routePath || route.path.startsWith(routePath)
  }

  const hasRequiredRole = (allowedRoles) => {
    if (!allowedRoles)
      return true
    return authStore.userRoles?.some(role => allowedRoles.includes(role))
  }

  const isModuleEnabled = (moduleName) => {
    if (!moduleName)
      return true
    return authStore.tenantData?.enabledModules?.includes(moduleName)
  }

  const handleModuleClick = (moduleName, moduleChildren) => {
    if (!moduleChildren?.length)
      return

    if (activeModule.value === moduleName) {
      activeModule.value = null
      return
    }

    activeModule.value = moduleName
    const homeRoute = moduleChildren.find(child =>
      child.route.includes('/home'),
    )?.route
    if (homeRoute && route.path !== homeRoute)
      router.push(homeRoute).catch(() => {})
  }

  const toggleExpand = (item) => {
    if (item.type === 'module') {
      handleModuleClick(item.name, item.children)
    }
    else if (item.type === 'sub') {
      // Toggle expansion for sub items
      if (expandedItems.value.has(item.name)) {
        expandedItems.value.delete(item.name)
      }
      else {
        expandedItems.value.add(item.name)
      }
    }
    else {
      // For core items, maintain exclusive expansion
      if (expandedItems.value.has(item.name)) {
        expandedItems.value.delete(item.name)
      }
      else {
        // Only clear other core items
        const coreItems = Array.from(expandedItems.value).filter(
          name => !name.includes('/'),
        ) // Assuming sub-items have '/' in their names
        coreItems.forEach(name => expandedItems.value.delete(name))
        expandedItems.value.add(item.name)
      }
    }
  }

  const isExpanded = (item) => {
    // For modules
    if (item.type === 'module') {
      return activeModule.value === item.name
    }

    // For core items
    if (item.type === 'core') {
      // Check if any child route matches current route
      const isPartOfCurrentRoute = item.children?.some(
        child =>
          route.path === child.route || route.path.startsWith(child.route),
      )
      return isPartOfCurrentRoute || expandedItems.value.has(item.name)
    }

    // For sub-items
    if (item.type === 'sub') {
      const isPartOfCurrentRoute = item.children?.some(
        child =>
          route.path === child.route || route.path.startsWith(child.route),
      )

      const isInActiveModule
        = activeModule.value && route.path.includes(item.route)

      const isManuallyExpanded = expandedItems.value.has(item.name)

      return isPartOfCurrentRoute || isInActiveModule || isManuallyExpanded
    }

    return expandedItems.value.has(item.name)
  }

  const findActiveModule = () => {
    // Check modules
    for (const module of menuItems.modules) {
      if (module.children) {
        const isActiveModule = module.children.some(
          child =>
            route.path === child.route || route.path.startsWith(child.route),
        )
        if (isActiveModule) {
          return module.name
        }
      }
    }
    return null
  }

  watch(
    () => route.path,
    () => {
      const newActiveModule = findActiveModule()
      if (newActiveModule) {
        activeModule.value = newActiveModule
      }
    },
    { immediate: true },
  )

  return {
    activeModule,
    isRouteActive,
    hasRequiredRole,
    isModuleEnabled,
    handleModuleClick,
    toggleExpand,
    isExpanded,
  }
}
